import { useEffect } from 'react';
import { Box, Layout } from '@zillow/constellation-10';
import PaymentsUpsell from '../Rent/PaymentsUpsell';
import type {
  YourRentalCreditReportingSectionDto,
  YourRentalPaymentSectionDto,
} from '@zg-rentals/ts-your-rental-api-client';
import { emitRentReportingUpsellView } from '../../analytics/events';
import PaymentCard from '../Rent/PaymentCard';
import PaymentCardError from '../Rent/PaymentCardError';
import CreditClimbCard from '../CreditClimb/CreditClimbCard';
import TransactionsTableError from '../Rent/TransactionsTableError';
import TransactionsTable from '../Rent/TransactionsTable';

type RentProps = {
  paymentsSection?: YourRentalPaymentSectionDto;
  creditReportingSection?: YourRentalCreditReportingSectionDto;
  listingAlias?: string;
};

const Rent = ({ paymentsSection, creditReportingSection, listingAlias }: RentProps) => {
  const showUpsell = !creditReportingSection && (!paymentsSection || !!paymentsSection.upsellCardType);

  useEffect(() => {
    if (showUpsell) {
      emitRentReportingUpsellView();
    }
  }, [showUpsell]);

  if (showUpsell) {
    return (
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          border: '{borderWidths.default} solid {colors.gray.300}',
          borderRadius: '{radii.300}',
        }}
        data-testid="RentReporting"
      >
        <PaymentsUpsell listingAlias={listingAlias} />
      </Box>
    );
  }

  const { paymentCard, transactionSection, error } = paymentsSection ?? {};

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'loose',
      }}
      data-testid="RentReporting"
    >
      <Layout.Root template="fluid12">
        {paymentCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <PaymentCard paymentCard={paymentCard} />
          </Layout.Content>
        )}
        {error && !paymentCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <PaymentCardError error={error} />
          </Layout.Content>
        )}
        {creditReportingSection?.creditClimbCard && (
          <Layout.Content css={{ gridColumnEnd: { base: 'span 12', lg: 'span 6', xxl: 'span 4' }, padding: '0' }}>
            <CreditClimbCard creditClimbCard={creditReportingSection.creditClimbCard} />
          </Layout.Content>
        )}
      </Layout.Root>
      {transactionSection && (
        <>
          <Box css={{ display: { base: 'block', lg: 'none' } }}>
            <TransactionsTable transactionSection={transactionSection} listingAlias={listingAlias} isDesktop={false} />
          </Box>
          <Box css={{ display: { base: 'none', lg: 'block' } }}>
            <TransactionsTable transactionSection={transactionSection} listingAlias={listingAlias} isDesktop={true} />
          </Box>
        </>
      )}
      {error && !transactionSection && <TransactionsTableError error={error} />}
    </Box>
  );
};

export default Rent;
